import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { useConcelhos, useDistritos } from '../hooks/useConcelhos';

/** Accent + case insensitive normalisation for search ("Óbidos" ~ "obidos"). */
const norm = (s: string): string =>
  s.normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();

export default function RiscoIndex() {
  useDocumentMeta(
    'Risco de incêndio por concelho em Portugal | Tutela IGNIS',
    'Previsão do risco de incêndio rural para os 278 concelhos de Portugal continental, quadrícula a quadrícula (1 km). Procure o seu concelho.',
    '/ignis/risco',
  );

  const distritos = useDistritos().data?.distritos ?? [];
  const concelhos = useConcelhos().data?.concelhos ?? [];
  const bySlug = useMemo(() => new Map(concelhos.map((c) => [c.slug, c])), [concelhos]);

  // ?q= is the SearchAction target (index.html WebSite schema): read it on mount,
  // mirror the box into the URL so results are linkable.
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get('q') ?? '');
  useEffect(() => {
    const current = searchParams.get('q') ?? '';
    if (query === current) return;
    setSearchParams(query ? { q: query } : {}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);
  const q = norm(query);
  const matches = useMemo(
    () =>
      q.length < 1
        ? []
        : concelhos
            .filter((c) => norm(c.nome).includes(q) || norm(c.distrito).includes(q))
            .sort((a, b) => a.nome.localeCompare(b.nome, 'pt'))
            .slice(0, 40),
    [q, concelhos],
  );

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-ink-950 text-mist">
      <div className="mx-auto max-w-4xl px-6 py-14">
        <nav className="font-mono text-xs text-mist-faint">
          <Link to="/ignis" className="hover:text-mist">IGNIS</Link>
          {' / '}
          <span className="text-mist-muted">Risco por concelho</span>
        </nav>

        <h1 className="mt-4 font-display text-3xl font-bold tracking-tight sm:text-4xl">
          Risco de incêndio por concelho
        </h1>
        <p className="mt-4 max-w-2xl text-lg leading-relaxed text-mist-muted">
          Previsão do risco de incêndio rural para os {concelhos.length || 278} concelhos de Portugal
          continental, quadrícula a quadrícula de 1 km. Procure o seu concelho ou escolha pelo distrito.
        </p>

        {/* Search */}
        <div className="mt-8">
          <label htmlFor="concelho-search" className="sr-only">
            Procurar concelho
          </label>
          <div className="relative max-w-md">
            <svg
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-mist-faint"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35m1.35-5.4a6.75 6.75 0 11-13.5 0 6.75 6.75 0 0113.5 0z" />
            </svg>
            <input
              id="concelho-search"
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Procurar o seu concelho (ex: Sintra, Guarda)…"
              autoComplete="off"
              className="w-full rounded-xl border border-ink-700 bg-ink-900 py-3 pl-10 pr-4 text-mist placeholder-mist-faint outline-none transition-colors focus:border-ember"
            />
          </div>
        </div>

        {/* Results: flat match list while searching, district grid otherwise */}
        {q.length >= 1 ? (
          <div className="mt-8">
            {matches.length === 0 ? (
              <p className="text-mist-muted">Nenhum concelho encontrado para “{query}”.</p>
            ) : (
              <>
                <p className="font-mono text-xs text-mist-faint">
                  {matches.length}{matches.length === 40 ? '+' : ''} resultado
                  {matches.length === 1 ? '' : 's'}
                </p>
                <ul className="mt-3 divide-y divide-ink-800 overflow-hidden rounded-xl border border-ink-700">
                  {matches.map((c) => (
                    <li key={c.slug}>
                      <Link
                        to={`/ignis/risco/${c.slug}`}
                        className="flex items-center justify-between px-4 py-3 transition-colors hover:bg-ink-800"
                      >
                        <span className="text-mist">{c.nome}</span>
                        <span className="font-mono text-xs text-mist-faint">{c.distrito}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        ) : (
          <div className="mt-10 space-y-8">
            {distritos.map((d) => (
              <section key={d.slug}>
                <h2 className="font-display text-lg font-bold">
                  <Link to={`/ignis/risco/distrito/${d.slug}`} className="hover:text-ember">
                    {d.nome}
                  </Link>{' '}
                  <span className="font-mono text-xs font-normal text-mist-faint">
                    {d.n_concelhos} concelhos
                  </span>
                </h2>
                <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5">
                  {d.concelhos.map((s) => {
                    const c = bySlug.get(s);
                    if (!c) return null;
                    return (
                      <Link
                        key={s}
                        to={`/ignis/risco/${s}`}
                        className="text-sm text-mist-muted transition-colors hover:text-mist"
                      >
                        {c.nome}
                      </Link>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
